import React, { useState, useEffect } from "react";
import { Table, Card, Tabs, Button, InputNumber, message, Space, Alert, Image, Typography } from "antd";
import axios from "axios";

const { Title, Text } = Typography;

const P_ChartModule = ({
  fileId,
  fileName,
  setSelectedFile,
  initialConfig,
}) => {
  const [activeTab, setActiveTab] = useState("input");
  const [inputData, setInputData] = useState({
    samples: []
  });
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const getAuthToken = () => localStorage.getItem("access_token");

  // Auth: Logout on 401
  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    window.location.reload();
  };

  useEffect(() => {
    if (fileId) {
      loadData(fileId);
    } else {
      // Initialize table with empty rows
      const numSamples = initialConfig?.num_samples || 0;
      const initialSamples = Array.from({ length: numSamples }, (_, i) => ({
        key: i,
        sample_size: 100, // Default sample size
        number_of_defects: 0,
      }));
      setInputData({ samples: initialSamples });
      setResults(null);
      setIsSaved(false);
      setActiveTab("input");
    }
  }, [fileId, initialConfig]);

  const loadData = async (id) => {
    try {
      const token = getAuthToken();
      if (!token) return message.error("Not authenticated.");
      setIsLoading(true);

      const res = await axios.get(
        `/api/retrieve_P_ChartModule_QualityAndPerformance/${id}/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Backend returns samples as array, without keys (usually)
      const samplesWithKeys = res.data.input_data.samples.map((s, idx) => ({
        ...s,
        key: idx
      }));

      setInputData({ samples: samplesWithKeys });
      setResults(res.data.output_data);
      setIsSaved(true);
      setActiveTab("output");
    } catch (err) {
      console.error("Load error:", err);
      if (err.response?.status === 401) handleLogout();
      message.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  const saveData = async () => {
    try {
      const token = getAuthToken();
      if (!token) return message.error("Not authenticated.");

      setIsLoading(true);

      // Prepare payload (no keys)
      const payload = {
        name: fileName || "Untitled P-Chart",
        input_data: {
          samples: inputData.samples.map(({ sample_size, number_of_defects }) => ({
            sample_size,
            number_of_defects
          }))
        },
      };

      let res;
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (fileId) {
        res = await axios.put(
          `/api/update_P_ChartModule_QualityAndPerformance/${fileId}/`,
          payload,
          config
        );
      } else {
        res = await axios.post(
          `/api/save_P_ChartModule_QualityAndPerformance/`,
          payload,
          config
        );
      }

      setResults(res.data.output_data);

      // Analyze the save response: Ensure the frontend updates the selectedFile.id immediately after saving to prevent duplicate entries.
      if (!fileId && setSelectedFile) {
        setSelectedFile({
          id: res.data.id,
          name: res.data.name,
          type: "P_ChartModule_QualityAndPerformance",
          inputData: res.data.input_data
        });
      }

      setIsSaved(true);
      setActiveTab("output");
      message.success(fileId ? "Updated successfully" : "Saved successfully");
    } catch (err) {
      console.error("Save error:", err);
      if (err.response?.status === 401) handleLogout();
      message.error(`Failed to save: ${err.response?.data?.error || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCellUpdate = (key, field, val) => {
    const updated = inputData.samples.map((s) =>
      s.key === key ? { ...s, [field]: val } : s
    );
    setInputData({ ...inputData, samples: updated });
  };

  const columns = [
    {
      title: "Sample #",
      dataIndex: "key",
      render: (key) => key + 1,
    },
    {
      title: "Sample Size (n)",
      dataIndex: "sample_size",
      render: (val, record) => (
        <InputNumber
          min={1}
          value={val}
          onChange={(v) => handleCellUpdate(record.key, "sample_size", v)}
        />
      ),
    },
    {
      title: "Number of Defects",
      dataIndex: "number_of_defects",
      render: (val, record) => (
        <InputNumber
          min={0}
          value={val}
          onChange={(v) => handleCellUpdate(record.key, "number_of_defects", v)}
        />
      ),
    },
  ];

  const renderInputTable = () => (
    <Card>
      <Table
        dataSource={inputData.samples}
        columns={columns}
        pagination={false}
        size="middle"
        scroll={{ y: 400 }}
      />
      <Button
        type="primary"
        onClick={saveData}
        loading={isLoading}
        style={{ marginTop: 24 }}
        block
      >
        {isSaved ? "Update Control Chart" : "Save & Generate Control Chart"}
      </Button>
    </Card>
  );

  const renderOutputTable = () => {
    if (!results) {
      return (
        <Alert
          message="Results not available"
          description="Please fill the input data and press 'Generate' to see the P-Chart."
          type="info"
          showIcon
        />
      );
    }

    const { table_data, summary_data } = results;

    const outputColumns = [
      { title: "Sample", dataIndex: "Sample", key: "Sample" },
      { title: "Number of Defects", dataIndex: "Number_of_Defects", key: "Number_of_Defects" },
      { title: "Fraction Defective", dataIndex: "Fraction_Defective", key: "Fraction_Defective" }
    ];

    return (
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Card title="Summary Statistics">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <Text><b>Total Defects:</b> {summary_data?.Total_Defects}</Text>
            <Text><b>Total Units Sampled:</b> {summary_data?.Total_units_sampled}</Text>
            <Text><b>Defect Rate (p-bar):</b> {summary_data?.Defect_rate_pbar}</Text>
            <Text><b>Std Dev of Proportions:</b> {summary_data?.Std_dev_of_proportions}</Text>
            <Text><b>Upper Control Limit (UCL):</b> {summary_data?.UCL}</Text>
            <Text><b>Center Line (CL):</b> {summary_data?.CL}</Text>
            <Text><b>Lower Control Limit (LCL):</b> {summary_data?.LCL}</Text>
          </div>
        </Card>

        {/* Look at the output_data from my sample response. Create a renderOutputTable function that maps these keys... */}
        <Card title="Detailed Analysis Table">
          <Table
            dataSource={table_data}
            columns={outputColumns}
            pagination={{ pageSize: 12 }}
            size="small"
          />
        </Card>

        {/* If the response includes a chart_url, implement an <Image> or <img> with a fallback state for the calculation result. */}
        {results.chart_url && (
          <Card title="P-Chart Visual (Quality And Performance)">
            <Image
              src={results.chart_url}
              alt="Control Chart"
              style={{ width: '100%', maxWidth: '800px' }}
              fallback="https://via.placeholder.com/800x400?text=Chart+rendering+error"
            />
          </Card>
        )}
      </Space>
    );
  };

  return (
    <Card
      title={fileName || "P-Chart Module (Quality And Performance)"}
      style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "input",
            label: "Inputs",
            children: renderInputTable()
          },
          {
            key: "output",
            label: "Results / Chart",
            disabled: !results,
            children: renderOutputTable()
          }
        ]}
      />
    </Card>
  );
};

export default P_ChartModule;
